import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetchEncryptedBlob, apiFetchWorkspaceBlob } from "../lib/authApi";
import { readChatImageCached, writeChatImageCached } from "../lib/chatImageDiskCache";
import { isDisplayableImageFile } from "../lib/displayableImageMime";
import {
  b64ToU8,
  decryptImageBytes,
  isE2eFileAttachment,
  isWorkspaceServerFileAttachment,
  type ChatFileAttachmentMeta,
} from "../lib/crypto";
import { ChatImageLightbox } from "./ChatImageLightbox";

type MessageFileBodyProps = {
  attachment: ChatFileAttachmentMeta;
  fetchE2eEncryptedBlob?: (blobId: string) => Promise<ArrayBuffer>;
};

function fileCacheKey(attachment: ChatFileAttachmentMeta, vault: boolean): string {
  if (isWorkspaceServerFileAttachment(attachment)) {
    return `wsf:${attachment.workspace_id}:${attachment.blob_id}`;
  }
  return vault ? `vaultfile:${attachment.blob_id}` : `file:${attachment.blob_id}`;
}

function triggerDownload(url: string, filename: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function formatSize(bytes: number | undefined): string | null {
  if (bytes === undefined || !Number.isFinite(bytes) || bytes < 1) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

export function MessageFileBody({ attachment, fetchE2eEncryptedBlob }: MessageFileBodyProps) {
  const [plainUrl, setPlainUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [thumbDecodeFailed, setThumbDecodeFailed] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const urlRef = useRef<string | null>(null);
  const loadPromiseRef = useRef<Promise<string | null> | null>(null);
  const cacheKey = fileCacheKey(attachment, Boolean(fetchE2eEncryptedBlob));
  const filename = attachment.filename || "file";
  const isPdf = attachment.mime === "application/pdf" || filename.toLowerCase().endsWith(".pdf");
  const isImageFile = isDisplayableImageFile(attachment.mime);
  const sizeLabel = formatSize(
    isWorkspaceServerFileAttachment(attachment) ? attachment.byte_length : undefined,
  );

  const fetchAndCache = useCallback(async (): Promise<string | null> => {
    if (urlRef.current) return urlRef.current;

    if (isWorkspaceServerFileAttachment(attachment)) {
      const buf = await apiFetchWorkspaceBlob(attachment.workspace_id, attachment.blob_id);
      const plain = new Uint8Array(buf);
      void writeChatImageCached(cacheKey, attachment.mime, plain);
      return URL.createObjectURL(new Blob([plain], { type: attachment.mime }));
    }

    if (!isE2eFileAttachment(attachment)) return null;

    const buf = fetchE2eEncryptedBlob
      ? await fetchE2eEncryptedBlob(attachment.blob_id)
      : await apiFetchEncryptedBlob(attachment.blob_id);
    const key = b64ToU8(attachment.key_b64);
    const nonce = b64ToU8(attachment.nonce_b64);
    const plain = decryptImageBytes(new Uint8Array(buf), nonce, key);
    void writeChatImageCached(cacheKey, attachment.mime, plain);
    return URL.createObjectURL(new Blob([new Uint8Array(plain)], { type: attachment.mime }));
  }, [attachment, cacheKey, fetchE2eEncryptedBlob]);

  useEffect(() => {
    let cancelled = false;
    setPlainUrl(null);
    setError(null);
    setThumbDecodeFailed(false);
    setLightboxOpen(false);
    loadPromiseRef.current = null;
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }

    if (isImageFile) {
      void (async () => {
        try {
          const cachedPlain = await readChatImageCached(cacheKey);
          if (cancelled) return;
          if (cachedPlain) {
            const url = URL.createObjectURL(new Blob([new Uint8Array(cachedPlain)], { type: attachment.mime }));
            if (cancelled) {
              URL.revokeObjectURL(url);
              return;
            }
            urlRef.current = url;
            setPlainUrl(url);
            return;
          }
          const url = await fetchAndCache();
          if (cancelled) {
            if (url) URL.revokeObjectURL(url);
            return;
          }
          if (!url) {
            setError("не удалось загрузить файл");
            return;
          }
          urlRef.current = url;
          setPlainUrl(url);
        } catch {
          if (!cancelled) setError("не удалось загрузить файл");
        }
      })();
    } else {
      void (async () => {
        try {
          const cachedPlain = await readChatImageCached(cacheKey);
          if (cancelled || !cachedPlain) return;
          const url = URL.createObjectURL(new Blob([new Uint8Array(cachedPlain)], { type: attachment.mime }));
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          urlRef.current = url;
          setPlainUrl(url);
        } catch {
          /* ignore */
        }
      })();
    }

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [cacheKey, attachment.mime, isImageFile, fetchAndCache]);

  const ensureLoaded = useCallback(async (): Promise<string | null> => {
    if (urlRef.current) return urlRef.current;
    if (loadPromiseRef.current) return loadPromiseRef.current;

    setError(null);
    setLoading(true);
    const p = (async (): Promise<string | null> => {
      try {
        const cached = await readChatImageCached(cacheKey);
        if (cached) {
          const url = URL.createObjectURL(new Blob([new Uint8Array(cached)], { type: attachment.mime }));
          if (urlRef.current) URL.revokeObjectURL(urlRef.current);
          urlRef.current = url;
          setPlainUrl(url);
          return url;
        }
        const url = await fetchAndCache();
        if (!url) {
          setError("не удалось загрузить файл");
          return null;
        }
        if (urlRef.current && urlRef.current !== url) URL.revokeObjectURL(urlRef.current);
        urlRef.current = url;
        setPlainUrl(url);
        return url;
      } catch {
        setError("не удалось загрузить файл");
        return null;
      } finally {
        setLoading(false);
        loadPromiseRef.current = null;
      }
    })();

    loadPromiseRef.current = p;
    return p;
  }, [cacheKey, attachment.mime, fetchAndCache]);

  const onDownload = useCallback(async () => {
    const url = urlRef.current ?? (await ensureLoaded());
    if (url) triggerDownload(url, filename);
  }, [ensureLoaded, filename]);

  const onOpen = useCallback(async () => {
    const url = urlRef.current ?? (await ensureLoaded());
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }, [ensureLoaded]);

  const showImagePreview = isImageFile && plainUrl && !thumbDecodeFailed && !error;
  const imageLoading = isImageFile && !plainUrl && !error;

  return (
    <div className={`bubble__file${plainUrl ? " bubble__file--ready" : ""}`}>
      {imageLoading ? (
        <span className="bubble__img-loading bubble__img-loading--default">…</span>
      ) : null}
      {showImagePreview ? (
        <>
          <button
            type="button"
            className="bubble__img-btn"
            onClick={() => setLightboxOpen(true)}
            aria-label="Открыть изображение крупнее"
          >
            <img
              src={plainUrl}
              alt=""
              className="bubble__img"
              loading="lazy"
              onError={() => setThumbDecodeFailed(true)}
            />
          </button>
          <ChatImageLightbox open={lightboxOpen} onClose={() => setLightboxOpen(false)} url={plainUrl} />
        </>
      ) : null}
      <span className="bubble__file-name" title={filename}>
        {filename}
        {sizeLabel ? <span className="bubble__file-meta"> · {sizeLabel}</span> : null}
      </span>
      {thumbDecodeFailed ? (
        <p className="bubble__file-hint">Предпросмотр недоступен для этого формата.</p>
      ) : !isImageFile ? (
        <p className="bubble__file-hint">Загрузка с сервера только по кнопке (из кэша — сразу, если уже открывали).</p>
      ) : null}
      {error ? <p className="bubble__file-err">{error}</p> : null}
      <div className="bubble__file-actions">
        {isPdf && !showImagePreview ? (
          <button
            type="button"
            className="bubble__file-btn"
            onClick={() => void onOpen()}
            disabled={loading}
          >
            {loading ? "…" : "Открыть"}
          </button>
        ) : null}
        <button type="button" className="bubble__file-btn" onClick={() => void onDownload()} disabled={loading}>
          {loading ? "…" : "Скачать"}
        </button>
      </div>
    </div>
  );
}
